import { hydrateItem } from '@zealous/template/timeline/hydrateItem'
import { templateItems } from '@zealous/template/timeline/templates'
import type {
  TimelineAsset,
  TimelineDoc,
  TimelineItem,
} from '@zealous/template/timeline/types'
import { ORIENTATIONS, type Orientation } from '@zealous/template/types'
import { defaultClipScene } from '@zealous/template/types/defaultClipScene'
import type { Layout } from '@zealous/template/types/layout'
import { LAYOUT_DEFAULTS } from '@zealous/template/types/layoutDefaults'
import { FPS } from '@zealous/template/types/video'
import applyTheme from '@zealous/template/utils/applyTheme'
import { liftToScenes } from '@zealous/template/types/liftToScenes'
import type {
  RenderConfigV2,
  RenderConfigV3,
} from '@zealous/template/types/renderConfigV2'

/**
 * The demo documents the Studio and the parity harness share (#546): the v1
 * scene of a layout as `RemotionStudio` registers it, and the generation-3 document
 * of the same layout — its template over one scene of a demo recording, the
 * default words, theme and episode, the watermark on. Tuning a blueprint in
 * the Studio and checking it with `bun compare:layouts` look at one document.
 */
const theme = defaultClipScene.theme
const speaker = defaultClipScene.paragraphs[0]!.speaker
const episode = {
  title: defaultClipScene.title,
  subtitle: defaultClipScene.subtitle,
  hostImageUrl: defaultClipScene.imageUrl,
  speakerName: speaker.name,
  speakerHandle: speaker.username,
}

/** The v1 scene for a layout, as `RemotionStudio` registers it. */
export function v1Scene(
  layout: Layout,
  orientation: Orientation,
  mediaUrl: string = defaultClipScene.mediaUrl,
) {
  const defaults = LAYOUT_DEFAULTS[layout]
  const style = applyTheme(defaults.defaultStyle, theme)
  return {
    ...defaultClipScene,
    layout,
    mediaUrl,
    showZealousLogo: false,
    style: {
      ...style,
      transcription: {
        ...style.transcription,
        ...defaults.transcriptions[orientation],
      },
    },
  }
}

/** The v2 document for a layout: the template over one scene of a demo recording. */
export function v2Doc(
  layout: Layout,
  orientation: Orientation,
  mediaUrl: string = defaultClipScene.mediaUrl,
): TimelineDoc {
  const { width, height } = ORIENTATIONS[orientation]
  const kind = LAYOUT_DEFAULTS[layout].kind
  const { tracks } = templateItems({
    layout,
    width,
    height,
    theme,
    episode,
    mediaKind: kind,
    span: { from: 0, duration: defaultClipScene.duration },
    scenes: [{ id: 'media-1', from: 0, duration: defaultClipScene.duration }],
  })
  const items: Record<string, TimelineItem> = {}
  for (const track of tracks)
    for (const item of track.items)
      items[item.id] = hydrateItem(item, { width, height, assetId: null })
  const scene = hydrateItem(
    {
      id: 'media-1',
      type: 'scene',
      from: 0,
      duration: defaultClipScene.duration,
      source: { kind: 'recording', recordingId: 'rec_demo' },
      sourceStart: 0,
      sourceEnd: defaultClipScene.duration,
    },
    { width, height, assetId: 'ass_demo' },
  )
  items[scene.id] = scene
  if (kind === 'video') {
    const view = hydrateItem(
      {
        id: 'media-1-view',
        type: 'view',
        scene: 'media-1',
        from: 0,
        duration: defaultClipScene.duration,
        transform: { x: 0, y: 0, width, height },
      },
      { width, height, assetId: null },
    )
    items[view.id] = view
  }
  const asset: TimelineAsset =
    kind === 'video'
      ? {
          id: 'ass_demo',
          type: 'video',
          filename: 'demo',
          size: 0,
          remoteUrl: mediaUrl,
          remoteFileKey: null,
          mimeType: 'video/mp4',
          durationInSeconds: defaultClipScene.duration,
          hasAudioTrack: true,
          width,
          height,
        }
      : {
          id: 'ass_demo',
          type: 'audio',
          filename: 'demo',
          size: 0,
          remoteUrl: mediaUrl,
          remoteFileKey: null,
          mimeType: 'audio/mp4',
          durationInSeconds: defaultClipScene.duration,
        }
  const backgroundAt = tracks.findIndex((t) => t.id === 'background')
  const docTracks = tracks.map((t) => ({
    id: t.id,
    items: t.items.map((i) => i.id),
    hidden: false,
    muted: false,
  }))
  // The picture where the media track always sat; the scenes at the bottom.
  if (kind === 'video')
    docTracks.splice(backgroundAt === -1 ? docTracks.length : backgroundAt, 0, {
      id: 'media',
      items: ['media-1-view'],
      hidden: false,
      muted: false,
    })
  docTracks.push({
    id: 'scenes',
    items: ['media-1'],
    hidden: false,
    muted: false,
  })
  return {
    fps: FPS,
    compositionWidth: width,
    compositionHeight: height,
    theme,
    tracks: docTracks,
    items,
    assets: {
      ass_demo: asset,
      'transcript:rec_demo': {
        id: 'transcript:rec_demo',
        type: 'transcript',
        recordingId: 'rec_demo',
        words: defaultClipScene.words,
        speakers: [
          {
            start: 0,
            end: defaultClipScene.duration,
            name: speaker.name,
            handle: speaker.username,
            avatarUrl: speaker.avatarUrl,
          },
        ],
        skips: [],
      },
    },
    template: { layout },
    watermark: true,
  }
}

/**
 * A generation-2 document and its lift, hydrated over the demo media and
 * words, for `compare:layouts --lift` (#541, eng D2): the same items must
 * draw the same pixels whichever generation they came in.
 */
export function liftPair(
  v2: RenderConfigV2,
  mediaUrl: string = defaultClipScene.mediaUrl,
): { v2: TimelineDoc; v3: TimelineDoc } {
  const orientation =
    v2.size.width === v2.size.height
      ? 'square'
      : v2.size.width > v2.size.height
        ? 'landscape'
        : 'portrait'
  const demo = v2Doc(v2.template?.layout ?? 'captions', orientation, mediaUrl)
  const recordingId = 'rec_1'
  const assets = {
    ass_demo: demo.assets.ass_demo!,
    [`transcript:${recordingId}`]: {
      ...(demo.assets['transcript:rec_demo'] as Extract<
        TimelineAsset,
        { type: 'transcript' }
      >),
      id: `transcript:${recordingId}`,
      recordingId,
    },
  }
  const hydrate = (config: RenderConfigV2 | RenderConfigV3): TimelineDoc => {
    const items: Record<string, TimelineItem> = {}
    for (const track of config.tracks)
      for (const item of track.items as Parameters<typeof hydrateItem>[0][])
        items[item.id] = hydrateItem(item, {
          width: config.size.width,
          height: config.size.height,
          assetId: 'source' in item && item.source ? 'ass_demo' : null,
        })
    return {
      ...demo,
      compositionWidth: config.size.width,
      compositionHeight: config.size.height,
      theme: config.theme,
      tracks: config.tracks.map((t) => ({
        id: t.id,
        items: t.items.map((i) => i.id),
        hidden: !!t.hidden,
        muted: !!t.muted,
      })),
      items,
      assets,
    }
  }
  return { v2: hydrate(v2), v3: hydrate(liftToScenes(v2).config) }
}
